import type { AnnotationDTO, AnnotationKind, Role, ShapeTarget, TextTarget } from "@/lib/protocol";
import type { TextLayerData } from "@/lib/text-layer";
import { coveredWords } from "@/lib/mark-presentation";

function stripBreaks(s: string): string {
  return s.replace(/-\n/g, "").replace(/\s*\n\s*/g, " ");
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  const firstWord = words[0];
  const lastWord = words[words.length - 1];
  if (firstWord === undefined || lastWord === undefined) return "?";
  const first = [...firstWord][0] ?? "?";
  const last = [...lastWord][0] ?? "?";
  return (words.length === 1 ? first : `${first}${last}`).toUpperCase();
}

export function slotOf(n: number): number {
  return Number.isInteger(n) ? ((n % 7) + 7) % 7 : 0;
}

export function draftChipText(kind: AnnotationKind, quote: string | null, pageN: number): string {
  if (kind !== "text") return pageN > 0 ? `Page ${pageN}` : "";
  return stripBreaks(quote ?? "");
}

export function quoteChipText(annotation: AnnotationDTO, textLayer: TextLayerData | null = null): string {
  if ("pageOnly" in annotation.target && annotation.target.pageOnly) return `page ${annotation.pageN}`;
  if (annotation.kind === "text") return draftChipText(annotation.kind, annotation.quote, annotation.pageN);
  const target = annotation.target as ShapeTarget;
  const words = textLayer?.page === target.page ? coveredWords(annotation.kind, target, textLayer) : null;
  return words ? `“${words}”` : shapeLabel(annotation.kind);
}

function shapeLabel(kind: Exclude<AnnotationKind, "text">): string {
  if (kind === "arrow") return "Arrow";
  if (kind === "ink" || kind === "line") return "Drawing";
  return "Box";
}

export interface QuoteParts { prefix: string; quote: string; suffix: string }

/**
 * The sentence a text comment's flagged words sit in (fix 2; mock composer.html
 * :164-167,379,416): prefix, then the quoted words (rendered in a <mark> by the
 * caller), then suffix. TextTarget.prefix/.quote/.suffix come straight off the
 * wire (server/src/services/anchors.ts CONTEXT_UTF16 = 32 UTF-16 units captured
 * on each side of the selection at anchor time) so this is the real surrounding
 * context the annotation carries, not sentence boundaries and not invented: a
 * selection near a page edge can carry an empty prefix or suffix, and that is
 * rendered empty rather than padded out.
 * Null for a shape or page-only mark, neither of which has text to quote, and null
 * when all three parts come back empty.
 *
 * That last case is a real bug this function caused, seen in a browser rather than in
 * a test: the caller (CommentCard.vue) guards the quote's CONTAINER on this returning
 * non-null, so an object whose three fields were all "" drew the container anyway.
 * Every card carried an empty white box with a coloured left edge and nothing in it.
 * The guard was asking "is there a text target" when the question the container needs
 * answered is "is there anything to show", and those came apart precisely when a text
 * annotation carried no captured context at all.
 */
export function quoteParts(annotation: AnnotationDTO): QuoteParts | null {
  if (annotation.kind !== "text") return null;
  if ("pageOnly" in annotation.target && annotation.target.pageOnly) return null;
  const target = annotation.target as TextTarget;
  const parts = { prefix: stripBreaks(target.prefix), quote: stripBreaks(target.quote), suffix: stripBreaks(target.suffix) };
  /* A selection at a page edge legitimately carries an empty prefix OR suffix, which
   * stays rendered empty as before. Only the all-empty case is nothing to show. */
  if (parts.prefix === "" && parts.quote === "" && parts.suffix === "") return null;
  return parts;
}

/** Fix 3 (mock composer.html:161,390): every card names the author's role next
 * to their name. Role values already read as words, so this is just the
 * capital the mock shows ("admin" -> "Admin"). */
export function roleLabel(role: Role): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/** Fix 7 (mock composer.html:249-258, decision 6 at composer.html:468-470): the
 * counter stays silent until COUNT_SHOW characters, and once past COUNT_MAX the
 * line names how far over. */
export const COUNT_SHOW = 3600;
export const COUNT_MAX = 4000;

/** Row NC: the three page-status note boxes (PageStatusControl, the contact
 * sheet's menu, BulkStatusMenu) had maxlength="280", which cuts a paste off in
 * silence. They now count and refuse the way the reply box does. 280 is the
 * server's own limit (server/src/services/pageStatus.ts:21); the counter shows
 * from 250, about the same tenth-from-the-end as COUNT_SHOW. */
export const NOTE_SHOW = 250;
export const NOTE_MAX = 280;

export function countLabel(length: number, max: number = COUNT_MAX): string {
  const fmt = (n: number) => n.toLocaleString();
  return length > max
    ? `${fmt(length)} of ${fmt(max)}, ${fmt(length - max)} over`
    : `${fmt(length)} of ${fmt(max)}`;
}
